import React from 'react';
import { Modal, Button, Alert, Badge } from 'react-bootstrap';
import { CheckCircle, XCircle, AlertTriangle, FileText, Database, Calendar, Building, Hash, Users, TrendingUp, X, Brain, Settings } from 'lucide-react';
import { useState, useEffect } from 'react';

const SieVerificationModal = ({
    show,
    onHide,
    verificationData,
    onConfirmImport,
    onCancel,
    isImporting
}) => {

    const [fiscalYearSettings, setFiscalYearSettings] = useState({});
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

    // Early return if no verification data
    /*if (!verificationData) {
        return null;
    }*/

    // Add useEffect after state declarations
    useEffect(() => {
        if (preview.fiscalYears && preview.fiscalYears.length > 0) {
            // Only configure the latest fiscal year
            const latestYear = preview.fiscalYears[0];
            setFiscalYearSettings({
                [latestYear.year]: {
                    accountingMethod: characteristics.accountingMethod.method === 'cash' ? 'cash' : 'accrual',
                    vatPeriod: characteristics.vatPeriod.period || 'quarterly',
                    isOpen: characteristics.fiscalYearStatus.status !== 'closed'
                }
            });
        }
    }, [verificationData]);

    const characteristics = verificationData?.characteristics || {
        accountingMethod: { method: 'unknown', confidence: 0, indicators: [] },
        vatPeriod: { period: 'unknown', confidence: 0, indicators: [] },
        fiscalYearStatus: { status: 'unknown', confidence: 0, indicators: [] }
    };


    // Safe access with fallbacks
    const validation = verificationData?.verification || {
        isValid: false,
        errors: [],
        warnings: [],
        summary: {
            totalErrors: 0,
            totalWarnings: 0,
            verificationsChecked: 0,
            accountsChecked: 0
        }
    };

    const preview = verificationData?.preview || {
        metadata: {},
        accountCount: 0,
        verificationCount: 0,
        transactionCount: 0,
        accountsWithTransactions: 0,
        fiscalYears: [],
        dateRange: {
            earliest: '',
            latest: ''
        },
        balanceSummary: {
            openingBalances: 0
        }
    };

    // Handle real metadata with safe access
    const metadata = preview?.metadata || {};
    const companyName = metadata.companyName || metadata.FNAMN || 'Unknown Company';
    const organizationNumber = metadata.organizationNumber || metadata.ORGNR || '';
    const programInfo = metadata.programInfo || metadata.PROGRAM || '';
    const sieType = metadata.sieType || metadata.SIETYP || '4';
    const generatedDate = metadata.generatedDate || metadata.GEN || '';

    const handleConfirm = () => {
        if (onConfirmImport) {

            console.log('Fiscal Year Settings:', fiscalYearSettings);
            onConfirmImport(fiscalYearSettings);
        }
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('sv-SE').format(num || 0);
    };


    // Add helper functions after formatNumber function
    const handleFiscalYearSettingChange = (year, setting, value) => {
        setFiscalYearSettings(prev => ({
            ...prev,
            [year]: {
                ...prev[year],
                [setting]: value
            }
        }));
    };

    const getConfidenceColor = (confidence) => {
        if (confidence >= 80) return '#10b981';
        if (confidence >= 60) return '#f59e0b';
        return '#ef4444';
    };

    const getMethodLabel = (method) => {
        switch (method) {
            case 'accrual': return 'Faktureringsmetoden';
            case 'cash': return 'Kontantmetoden';
            default: return 'Okänd';
        }
    };

    const getVatPeriodLabel = (period) => {
        switch (period) {
            case 'monthly': return 'Månadsvis';
            case 'quarterly': return 'Kvartalsvis';
            case 'yearly': return 'Årsvis';
            default: return 'Okänd';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'open': return 'Öppet';
            case 'closed': return 'Stängt';
            default: return 'Okänt';
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" centered>
            <style>{`
                .sie-modal-content {
                    border-radius: 12px;
                    border: none;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                }
                
                .sie-modal-header {
                    background: #f8fafc;
                    color: #1e293b;
                    padding: 1.5rem 2rem;
                    border-bottom: 1px solid #e2e8f0;
                    position: relative;
                }
                
                .sie-modal-body {
                    padding: 2rem;
                    background: #ffffff;
                }
                
                .validation-summary {
                    background: #f1f5f9;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 2rem;
                    border-left: 4px solid ${validation.isValid ? '#10b981' : '#ef4444'};
                }
                
                .validation-summary.valid {
                    background: #f0fdf4;
                    border-left-color: #10b981;
                }
                
                .validation-summary.invalid {
                    background: #fef2f2;
                    border-left-color: #ef4444;
                }
                
                .validation-text {
                    margin: 0;
                    font-size: 0.875rem;
                    color: #374151;
                    font-weight: 500;
                }
                
                .metric-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
                    gap: 1rem;
                    margin-bottom: 2rem;
                }
                
                .metric-card {
                    background: white;
                    border-radius: 8px;
                    padding: 1.25rem;
                    text-align: center;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                }
                
                .metric-card:hover {
                    border-color: #cbd5e1;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }
                
                .metric-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 0.75rem;
                    background: #f1f5f9;
                    color: #64748b;
                }
                
                .metric-value {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 0.25rem;
                }
                
                .metric-label {
                    font-size: 0.75rem;
                    color: #64748b;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }
                
                .info-section {
                    background: white;
                    border-radius: 8px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                    border: 1px solid #e2e8f0;
                }
                
                .section-title {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .table-container {
                    background: white;
                    border-radius: 8px;
                    overflow: hidden;
                    border: 1px solid #e2e8f0;
                    margin-bottom: 1.5rem;
                }
                
                .fiscal-years-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.875rem;
                }
                
                .fiscal-years-table thead {
                    background: #f8fafc;
                }
                
                .fiscal-years-table th {
                    padding: 0.75rem 1rem;
                    text-align: left;
                    font-weight: 600;
                    color: #374151;
                    border-bottom: 1px solid #e2e8f0;
                    font-size: 0.8rem;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }
                
                .fiscal-years-table td {
                    padding: 0.75rem 1rem;
                    border-bottom: 1px solid #f1f5f9;
                    color: #374151;
                }
                
                .fiscal-years-table tbody tr:hover {
                    background-color: #f8fafc;
                }
                
                .fiscal-years-table tbody tr:last-child td {
                    border-bottom: none;
                }
                
                .year-cell {
                    font-weight: 600;
                    color: #1e293b;
                }
                
                .period-cell {
                    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                    font-size: 0.8rem;
                    color: #6b7280;
                }
                
                .company-info-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 1rem;
                }
                
                .company-card {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    transition: all 0.2s ease;
                }
                
                .company-card:hover {
                    border-color: #cbd5e1;
                    background: #f1f5f9;
                }
                
                .company-card.primary {
                    background: #dbeafe;
                    border-color: #93c5fd;
                }
                
                .company-card-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 6px;
                    background: #e2e8f0;
                    color: #64748b;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                
                .company-card.primary .company-card-icon {
                    background: #3b82f6;
                    color: white;
                }
                
                .company-card-content {
                    flex: 1;
                    min-width: 0;
                }
                
                .company-card-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                    margin-bottom: 0.25rem;
                }
                
                .company-card-value {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #1e293b;
                    line-height: 1.4;
                    word-break: break-word;
                }
                
                .additional-info {
                    margin-top: 1.5rem;
                    padding-top: 1.5rem;
                    border-top: 1px solid #e2e8f0;
                }
                
                .info-cards-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 1rem;
                }
                
                .info-card {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    padding: 1rem;
                    text-align: center;
                }
                
                .info-card-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                    margin-bottom: 0.5rem;
                }
                
                .info-card-value {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #1e293b;
                    line-height: 1.4;
                }
                
                .warning-item {
                    background: #fef3c7;
                    border: 1px solid #f59e0b;
                    border-radius: 6px;
                    padding: 1rem;
                    margin-bottom: 0.5rem;
                    display: flex;
                    align-items: start;
                    gap: 0.75rem;
                }
                
                .warning-icon {
                    color: #d97706;
                    flex-shrink: 0;
                    margin-top: 0.125rem;
                }
                
                .warning-content {
                    flex: 1;
                }
                
                .warning-type {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #92400e;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                    margin-bottom: 0.25rem;
                }
                
                .warning-message {
                    font-size: 0.875rem;
                    color: #92400e;
                    margin: 0;
                }
                
                .no-data-message {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 1.5rem;
                    background: #fef3c7;
                    border-radius: 6px;
                    color: #92400e;
                    font-size: 0.875rem;
                    margin-bottom: 1.5rem;
                    border: 1px solid #f59e0b;
                    font-weight: 500;
                }
                
                .modal-actions {
                    background: #f8fafc;
                    padding: 1.5rem 2rem;
                    border-top: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                
                .action-button {
                    border-radius: 6px;
                    padding: 0.75rem 1.5rem;
                    font-weight: 600;
                    font-size: 0.875rem;
                    border: none;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .btn-cancel {
                    background: #f1f5f9;
                    color: #64748b;
                    border: 1px solid #e2e8f0;
                }
                
                .btn-cancel:hover {
                    background: #e2e8f0;
                    color: #475569;
                }
                
                .btn-import {
                    background: #3b82f6;
                    color: white;
                }
                
                .btn-import:hover {
                    background: #2563eb;
                }
                
                .btn-import:disabled {
                    background: #94a3b8;
                    cursor: not-allowed;
                }
                
                .loading-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid white;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .close-button {
                    position: absolute;
                    top: 1rem;
                    right: 1.5rem;
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                    color: #64748b;
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                }
                
                .close-button:hover {
                    background: #e2e8f0;
                    color: #475569;
                }
                
                /* Responsive design */
                @media (max-width: 768px) {
                    .company-info-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .info-cards-grid {
                        grid-template-columns: 1fr;
                    }
                }

                .characteristics-section {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                }
                .characteristics-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }

                .characteristic-card {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1rem;
                    text-align: center;
                    transition: all 0.2s ease;
                }

                .characteristic-card:hover {
                    border-color: #cbd5e1;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }

                .characteristic-header {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    margin-bottom: 0.75rem;
                }

                .characteristic-title {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #374151;
                    margin: 0 0 0.5rem 0;
                    text-align: center;
                }

                .confidence-badge {
                    padding: 0.25rem 0.5rem;
                    border-radius: 12px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: white;
                }

                .characteristic-value {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                    text-align: center;
                }

                @media (max-width: 768px) {
                    .characteristics-grid {
                        grid-template-columns: 1fr;
                    }
    
                    .company-info-grid {
                        grid-template-columns: 1fr;
                    }
    
                    .info-cards-grid {
                        grid-template-columns: 1fr;
                    }
                }

                .characteristic-indicators {
                    font-size: 0.75rem;
                    color: #6b7280;
                    line-height: 1.4;
                }

                .advanced-toggle {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 1rem;
                    cursor: pointer;
                    padding: 0.75rem;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    background: #f9fafb;
                    transition: all 0.2s ease;
                }

                .advanced-toggle:hover {
                    background: #f3f4f6;
                    border-color: #d1d5db;
                }

                .fiscal-year-config {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1.25rem;
                }

                .settings-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 1rem;
                }

                .setting-group {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .setting-label {
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: #374151;
                }

                .modern-select {
                    width: 100%;
                    padding: 0.5rem 0.75rem;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    font-size: 0.875rem;
                    background: white;
                    color: #374151;
                }

                .modern-select:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .toggle-switch {
                    position: relative;
                    display: inline-block;
                    width: 44px;
                    height: 24px;
                }

                .toggle-input {
                    opacity: 0;
                    width: 0;
                    height: 0;
                }

                .toggle-slider {
                    position: absolute;
                    cursor: pointer;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: #cbd5e1;
                    transition: 0.3s;
                    border-radius: 24px;
                }

                .toggle-slider:before {
                    position: absolute;
                    content: "";
                    height: 18px;
                    width: 18px;
                    left: 3px;
                    bottom: 3px;
                    background-color: white;
                    transition: 0.3s;
                    border-radius: 50%;
                }

                .toggle-input:checked + .toggle-slider {
                    background-color: #3b82f6;
                }

                .toggle-input:checked + .toggle-slider:before {
                    transform: translateX(20px);
                }
            `}</style>

            <div className="sie-modal-content">
                {/* Header */}
                <div className="sie-modal-header">
                    <button className="close-button" onClick={onHide}>
                        <X size={16} />
                    </button>
                    <div className="d-flex align-items-center gap-3">
                        <FileText size={24} color="#64748b" />
                        <div>
                            <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                SIE-fil verifiering
                            </h3>
                            <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                                Kontrollera och importera bokföringsdata
                            </p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="sie-modal-body">
                    {/* Simple validation summary */}
                    {/*<div className={`validation-summary ${validation.isValid ? 'valid' : 'invalid'}`}>
                        <p className="validation-text">
                            {validation.isValid
                                ? '✓ Filen är redo för import'
                                : '⚠ Problem hittades som behöver åtgärdas'
                            }
                            {validation.summary?.totalWarnings > 0 &&
                                ` • ${validation.summary.totalWarnings} varning${validation.summary.totalWarnings !== 1 ? 'ar' : ''}`
                            }
                        </p>
                    </div>*/}

                    {/* Metrics Grid */}
                    <div className="metric-grid">
                        <div className="metric-card">
                            <div className="metric-icon">
                                <Hash size={20} />
                            </div>
                            <div className="metric-value">{formatNumber(preview.accountCount)}</div>
                            <div className="metric-label">Konton</div>
                        </div>

                        <div className="metric-card">
                            <div className="metric-icon">
                                <FileText size={20} />
                            </div>
                            <div className="metric-value">{formatNumber(preview.verificationCount)}</div>
                            <div className="metric-label">Verifikationer</div>
                        </div>

                        <div className="metric-card">
                            <div className="metric-icon">
                                <TrendingUp size={20} />
                            </div>
                            <div className="metric-value">{formatNumber(preview.transactionCount)}</div>
                            <div className="metric-label">Transaktioner</div>
                        </div>

                        <div className="metric-card">
                            <div className="metric-icon">
                                <Users size={20} />
                            </div>
                            <div className="metric-value">{formatNumber(preview.accountsWithTransactions)}</div>
                            <div className="metric-label">Aktiva konton</div>
                        </div>
                    </div>

                    {/* Company Info */}
                    <div className="info-section">
                        <div className="section-title">
                            <Building size={18} />
                            Företagsinformation
                        </div>

                        <div className="company-info-grid">
                            <div className="company-card primary">
                                <div className="company-card-icon">
                                    <Building size={20} />
                                </div>
                                <div className="company-card-content">
                                    <div className="company-card-label">Företagsnamn</div>
                                    <div className="company-card-value">{companyName}</div>
                                </div>
                            </div>

                            <div className="company-card">
                                <div className="company-card-icon">
                                    <Hash size={20} />
                                </div>
                                <div className="company-card-content">
                                    <div className="company-card-label">Organisationsnummer</div>
                                    <div className="company-card-value">{organizationNumber || 'Ej angivet'}</div>
                                </div>
                            </div>

                            <div className="company-card">
                                <div className="company-card-icon">
                                    <FileText size={20} />
                                </div>
                                <div className="company-card-content">
                                    <div className="company-card-label">SIE-typ</div>
                                    <div className="company-card-value">Typ {sieType}</div>
                                </div>
                            </div>

                            <div className="company-card">
                                <div className="company-card-icon">
                                    <Database size={20} />
                                </div>
                                <div className="company-card-content">
                                    <div className="company-card-label">Skapad med</div>
                                    <div className="company-card-value">{programInfo || 'Okänt program'}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* AI Characteristics & Settings */}
                    {characteristics && (
                        <div className="characteristics-section">
                            <div className="section-title">
                                <Brain size={18} />
                                AI-analys och inställningar
                            </div>
                            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                                Automatiskt identifierade egenskaper för det senaste räkenskapsåret:
                            </p>

                            <div className="characteristics-grid">
                                <div className="characteristic-card">
                                    <div className="characteristic-header">
                                        <h4 className="characteristic-title">Redovisningsmetod</h4>
                                        <span
                                            className="confidence-badge"
                                            style={{ backgroundColor: getConfidenceColor(characteristics.accountingMethod.confidence) }}
                                        >
                                            {characteristics.accountingMethod.confidence}% säker
                                        </span>
                                    </div>
                                    <div className="characteristic-value">
                                        {getMethodLabel(characteristics.accountingMethod.method)}
                                    </div>
                                </div>

                                <div className="characteristic-card">
                                    <div className="characteristic-header">
                                        <h4 className="characteristic-title">Momsperiod</h4>
                                        <span
                                            className="confidence-badge"
                                            style={{ backgroundColor: getConfidenceColor(characteristics.vatPeriod.confidence) }}
                                        >
                                            {characteristics.vatPeriod.confidence}% säker
                                        </span>
                                    </div>
                                    <div className="characteristic-value">
                                        {getVatPeriodLabel(characteristics.vatPeriod.period)}
                                    </div>
                                </div>

                                <div className="characteristic-card">
                                    <div className="characteristic-header">
                                        <h4 className="characteristic-title">Årsstatus</h4>
                                        <span
                                            className="confidence-badge"
                                            style={{ backgroundColor: getConfidenceColor(characteristics.fiscalYearStatus.confidence) }}
                                        >
                                            {characteristics.fiscalYearStatus.confidence}% säker
                                        </span>
                                    </div>
                                    <div className="characteristic-value">
                                        {getStatusLabel(characteristics.fiscalYearStatus.status)}
                                    </div>
                                </div>
                            </div>

                            <div
                                className="advanced-toggle"
                                onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                            >
                                <Settings size={16} />
                                <span style={{ fontWeight: '500' }}>
                                    Ändra inställningar för import
                                </span>
                                <div style={{ marginLeft: 'auto' }}>
                                    {showAdvancedSettings ? '▲' : '▼'}
                                </div>
                            </div>

                            {showAdvancedSettings && preview.fiscalYears && preview.fiscalYears.length > 0 && (
                                <div className="fiscal-year-config">
                                    {(() => {
                                        const latestYear = preview.fiscalYears[0];
                                        return (
                                            <div>
                                                <div style={{ marginBottom: '1rem' }}>
                                                    <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', margin: 0 }}>
                                                        Räkenskapsår {latestYear.year}
                                                    </h4>
                                                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                                        {latestYear.startDate} → {latestYear.endDate}
                                                    </span>
                                                </div>

                                                <div className="settings-grid">
                                                    <div className="setting-group">
                                                        <label className="setting-label">Redovisningsmetod</label>
                                                        <select
                                                            className="modern-select"
                                                            value={fiscalYearSettings[latestYear.year]?.accountingMethod || 'accrual'}
                                                            onChange={(e) => handleFiscalYearSettingChange(latestYear.year, 'accountingMethod', e.target.value)}
                                                        >
                                                            <option value="accrual">Faktureringsmetoden</option>
                                                            <option value="cash">Kontantmetoden</option>
                                                        </select>
                                                    </div>

                                                    <div className="setting-group">
                                                        <label className="setting-label">Momsperiod</label>
                                                        <select
                                                            className="modern-select"
                                                            value={fiscalYearSettings[latestYear.year]?.vatPeriod || 'quarterly'}
                                                            onChange={(e) => handleFiscalYearSettingChange(latestYear.year, 'vatPeriod', e.target.value)}
                                                        >
                                                            <option value="monthly">Månadsvis</option>
                                                            <option value="quarterly">Kvartalsvis</option>
                                                            <option value="yearly">Årsvis</option>
                                                        </select>
                                                    </div>

                                                    <div className="setting-group">
                                                        <label className="setting-label">Räkenskapsårsstatus</label>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                            <span style={{ fontSize: '0.875rem' }}>Stängt</span>
                                                            <label className="toggle-switch">
                                                                <input
                                                                    type="checkbox"
                                                                    className="toggle-input"
                                                                    checked={fiscalYearSettings[latestYear.year]?.isOpen || false}
                                                                    onChange={(e) => handleFiscalYearSettingChange(latestYear.year, 'isOpen', e.target.checked)}
                                                                />
                                                                <span className="toggle-slider"></span>
                                                            </label>
                                                            <span style={{ fontSize: '0.875rem' }}>Öppet</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    )}


                    {/* Period Info */}
                    <div className="info-section">
                        <div className="section-title">
                            <Calendar size={18} />
                            Räkenskapsperiod
                        </div>

                        {/* Fiscal Years Table */}
                        {preview.fiscalYears && preview.fiscalYears.length > 0 ? (
                            <div className="table-container">
                                <table className="fiscal-years-table">
                                    <thead>
                                        <tr>
                                            <th>Räkenskapsår</th>
                                            <th>Startdatum</th>
                                            <th>Slutdatum</th>
                                            <th>Period</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.fiscalYears.map((year, index) => (
                                            <tr key={index}>
                                                <td className="year-cell">{year.year || `År ${index + 1}`}</td>
                                                <td>{year.startDate || '-'}</td>
                                                <td>{year.endDate || '-'}</td>
                                                <td className="period-cell">
                                                    {year.startDate && year.endDate
                                                        ? `${year.startDate} → ${year.endDate}`
                                                        : 'Ej specificerad'
                                                    }
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="no-data-message">
                                <AlertTriangle size={16} />
                                <span>Ingen räkenskapsårsinformation tillgänglig i filen</span>
                            </div>
                        )}

                        {/* Additional Info Cards */}
                        <div className="additional-info">
                            <div className="info-cards-grid">
                                {/* Data Range */}
                                {(preview.dateRange?.earliest || preview.dateRange?.latest) && (
                                    <div className="info-card">
                                        <div className="info-card-label">Dataomfång</div>
                                        <div className="info-card-value">
                                            {preview.dateRange.earliest && preview.dateRange.latest
                                                ? `${preview.dateRange.earliest} → ${preview.dateRange.latest}`
                                                : preview.dateRange.earliest || preview.dateRange.latest || 'Okänt'
                                            }
                                        </div>
                                    </div>
                                )}

                                {/* Balance Info */}
                                <div className="info-card">
                                    <div className="info-card-label">Öppningsbalanser</div>
                                    <div className="info-card-value">{preview.balanceSummary?.openingBalances || 0} st</div>
                                </div>

                                {/* Generated Date */}
                                {generatedDate && (
                                    <div className="info-card">
                                        <div className="info-card-label">Skapad datum</div>
                                        <div className="info-card-value">
                                            {generatedDate.length === 8
                                                ? `${generatedDate.substring(0, 4)}-${generatedDate.substring(4, 6)}-${generatedDate.substring(6, 8)}`
                                                : generatedDate
                                            }
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Warnings */}
                    {validation.warnings && validation.warnings.length > 0 && (
                        <div className="info-section">
                            <div className="section-title">
                                <AlertTriangle size={18} />
                                Varningar
                            </div>
                            {validation.warnings.map((warning, index) => (
                                <div key={index} className="warning-item">
                                    <AlertTriangle size={16} className="warning-icon" />
                                    <div className="warning-content">
                                        <div className="warning-type">{warning.type || 'Varning'}</div>
                                        <p className="warning-message">{warning.message || 'Okänd varning'}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="modal-actions">
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        {validation.isValid
                            ? 'Filen är redo att importeras'
                            : 'Åtgärda felen innan import'
                        }
                    </div>
                    <div className="d-flex gap-3">
                        <button
                            className="action-button btn-cancel"
                            onClick={handleCancel}
                            disabled={isImporting}
                        >
                            Avbryt
                        </button>
                        <button
                            className="action-button btn-import"
                            onClick={handleConfirm}
                            disabled={!validation.isValid || isImporting}
                        >
                            {isImporting ? (
                                <>
                                    <div className="loading-spinner"></div>
                                    Importerar...
                                </>
                            ) : (
                                <>
                                    <Database size={16} />
                                    Importera fil
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default SieVerificationModal;